// Uyarlanmış kopya — emsal: beltasapp/frontend/src/shared/lib/form/use-record-version.tsx.
// Düzenleme kilidi (heartbeat) kapsam dışıdır; çakışma uyarısı rozet yerine satır içi `Alert`tir.
import { useRef, useState, type ReactNode } from 'react'

import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'
import { RecordConflictAlert } from '@/components/feedback/RecordConflictAlert'
import {
  recordChangedError,
  recordChangedMessage,
  resolveVersion,
} from '@/lib/form/record-conflict'
import { messages } from '@/lib/messages'
import type { Problem } from '@/lib/problem'

export interface RecordVersionOptions {
  /** Kaydın kimliği. Değişirse sabitlenen ve kendi yazmalardan gelen sürüm bırakılır. */
  recordId: string | number | null | undefined
  /** Sunucudan yüklenen en güncel sürüm (`query.data.version`); yeni ya da yüklenmemiş kayıtta `null`. */
  loaded: number | null | undefined
  /**
   * Kaydedilmemiş yerel değişiklik var. Kirliyken düzenlemenin başladığı sürüm korunur: arka plan
   * tazelemesi (gerçek zamanlı `data-changed` invalidation'ı dahil) daha yeni bir sürüm getirse bile
   * yazma eski sürümle gider ve 409 alır. Aksi hâlde başkasının değişikliği sessizce ezilirdi.
   * Açılışta bir kez alınan anlık görüntüyle çalışan editörler (taslak sunucudan bir kez kopyalanır)
   * her zaman `true` verir.
   */
  dirty: boolean
  /** "Yeniden yükle": kaydı tazeler ve ekranın yerel durumunu ondan kurar (taslağı atar). */
  reload?: () => Promise<unknown>
}

/**
 * Tek kayıt editörünün sürüm zinciri. Her sürümlü yazma `current()` gönderir; yanıtın döndürdüğü
 * sürüm `advance()`'e verilir, böylece bölünmüş kaydetme (ör. grup izinleri → kapsam) zincirlenir.
 * Bayat yazmanın 409'u `catchConflict`'e gider ve `alert` görünür.
 */
export interface RecordVersion {
  /** Sonraki yazmada gönderilecek `version` (hiçbir şey yüklenmediyse 0). */
  current: () => number
  /** Yazmanın döndürdüğü sürümü kaydeder (nullish yok sayılır), görünen çakışmayı temizler; `current()` döner. */
  advance: (next: number | null | undefined) => number
  /** `error` bayat sürüm 409'u ise `true` döner ve `alert` görünür; çağıran genel hata toast'ını atlar. */
  catchConflict: (error: unknown) => boolean
  conflict: Problem | null
  /** Çakışma uyarısı ("X 14:05'te değiştirdi" + "Yeniden yükle"); çakışma yoksa `null`. */
  alert: ReactNode
  /** Kirli ekranda yeniden yükleme onayı; ekranın JSX'ine konur. */
  dialog: ReactNode
}

interface Stamp {
  id: string | number
  version: number
}

export function useRecordVersion({
  recordId,
  loaded,
  dirty,
  reload,
}: RecordVersionOptions): RecordVersion {
  const id = recordId ?? null
  const loadedVersion = loaded ?? null
  // Kendi yazmalar bir sonraki render'dan önce biter; zincirli çağrılar state değil ref okur.
  const own = useRef<Stamp | null>(null)
  const [pin, setPin] = useState<Stamp | null>(null)
  const [conflict, setConflict] = useState<Problem | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reloading, setReloading] = useState(false)
  const [reloadFailed, setReloadFailed] = useState(false)

  // Düzenlemenin başladığı sürümü sabitle; kaydedilince ya da atılınca bırak.
  if (dirty && id != null && loadedVersion != null && pin?.id !== id) {
    setPin({ id, version: loadedVersion })
  }
  if (!dirty && pin != null) setPin(null)

  function current(): number {
    return resolveVersion({
      dirty,
      pinned: pin != null && pin.id === id ? pin.version : null,
      loaded: loadedVersion,
      own: own.current != null && own.current.id === id ? own.current.version : null,
    })
  }

  function advance(next: number | null | undefined): number {
    if (next != null && id != null) {
      own.current = { id, version: next }
      setConflict(null)
    }
    return current()
  }

  function catchConflict(error: unknown): boolean {
    const changed = recordChangedError(error)
    if (changed) setConflict(changed)
    return changed != null
  }

  async function doReload() {
    setConfirmOpen(false)
    setReloading(true)
    setReloadFailed(false)
    try {
      await reload?.()
      // Ekran yerel durumunu yeni kayıttan kurduktan sonra eski sürümü bırak; sonraki render yeni
      // yüklenen sürümü yeniden sabitler.
      own.current = null
      setPin(null)
      setConflict(null)
    } catch {
      // Uyarı ve yerel taslak yerinde kalır; kullanıcı yeniden dener.
      setReloadFailed(true)
    } finally {
      setReloading(false)
    }
  }

  const alert = conflict ? (
    <RecordConflictAlert
      label={recordChangedMessage(conflict)}
      pending={reloading}
      failed={reloadFailed}
      onReload={() => (dirty ? setConfirmOpen(true) : void doReload())}
    />
  ) : null

  const dialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={messages.recordConflict.confirmTitle}
      description={messages.recordConflict.confirmText}
      confirmLabel={messages.recordConflict.reload}
      onConfirm={() => void doReload()}
    />
  )

  return { current, advance, catchConflict, conflict, alert, dialog }
}
